import dbscan from "@cdxoo/dbscan";
import * as THREE from "three";

const computeFaceNormals = (
  posAttr: THREE.BufferAttribute | THREE.InterleavedBufferAttribute,
  indexAttr: THREE.BufferAttribute | null
): number[][] => {
  const normals: number[][] = [];
  if (indexAttr) {
    // Indexed geometry
    for (let i = 0; i < indexAttr.count; i += 3) {
      const a = indexAttr.getX(i);
      const b = indexAttr.getX(i + 1);
      const c = indexAttr.getX(i + 2);

      const vA = new THREE.Vector3().fromBufferAttribute(posAttr, a);
      const vB = new THREE.Vector3().fromBufferAttribute(posAttr, b);
      const vC = new THREE.Vector3().fromBufferAttribute(posAttr, c);

      const cb = new THREE.Vector3().subVectors(vC, vB);
      const ab = new THREE.Vector3().subVectors(vA, vB);
      cb.cross(ab).normalize();

      normals.push([cb.x, cb.y, cb.z]);
    }
  } else {
    // Non-indexed geometry
    for (let i = 0; i < posAttr.count; i += 3) {
      const vA = new THREE.Vector3().fromBufferAttribute(posAttr, i);
      const vB = new THREE.Vector3().fromBufferAttribute(posAttr, i + 1);
      const vC = new THREE.Vector3().fromBufferAttribute(posAttr, i + 2);

      const cb = new THREE.Vector3().subVectors(vC, vB);
      const ab = new THREE.Vector3().subVectors(vA, vB);
      cb.cross(ab).normalize();

      normals.push([cb.x, cb.y, cb.z]);
    }
  }

  return normals;
};

/**
 * Cluster similar normals using DBSCAN. If size of largest cluster exceeds threshold, assume model is based.
 */
export const isBasedModel = (geometry: THREE.BufferGeometry): boolean => {
  // Testing found that >99% of scans had less than 5 cells in their largest cluster, and all based models had more than 5 (most had a lot more).
  const INTRAORAL_SCAN_MAX_CELLS = 5;

  const posAttr = geometry.getAttribute("position");
  const indexAttr = geometry.index;

  // Compute face normals
  const normals: number[][] = computeFaceNormals(posAttr, indexAttr);

  // Subsample normals
  const targetN = 5000;
  const step = Math.max(1, Math.floor(normals.length / targetN));
  const pointsSub = normals.filter((_, i) => i % step === 0);

  // Group normals by similarity
  const epsilon = 1e-3;
  const { clusters } = dbscan({
    dataset: pointsSub,
    epsilon,
    minimumPoints: 1,
    distanceFunction: (a: number[], b: number[]) => {
      const dx = a[0] - b[0];
      const dy = a[1] - b[1];
      const dz = a[2] - b[2];
      return Math.sqrt(dx * dx + dy * dy + dz * dz);
    }
  });
  if (!clusters.length) {
    return false;
  }

  // Find largest group of similar normals
  clusters.sort((a: number[], b: number[]) => b.length - a.length);
  const largestGroup = clusters[0];

  return largestGroup.length > INTRAORAL_SCAN_MAX_CELLS;
};
